import {
  FileViewer as FileViewerLib,
  Preview,
  PreviewType,
} from "@dropbox/file-viewer";
import { fetchFileViewerIntlForLocale } from "@dropbox/file-viewer/lang/get_lang";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { makeApiV2ClientShim } from "@mirage/mosaics/FileViewer/ApiV2Shim";
import i18n from "@mirage/translations";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import style from "./FileViewer.module.css";
import { getFileViewerLogger } from "./FileViewerLogger";

import type { SourceContext } from "@dropbox/file-viewer/core/logging/constants";
import type { FVError } from "@dropbox/file-viewer/core/types/errors";
import type { PreviewProps } from "@dropbox/file-viewer/core/types/props";
import type { FileViewerProps as FileViewerLibProps } from "@dropbox/file-viewer/types";
import type { FileViewerProps } from "@mirage/mosaics/FileViewer/types";

export const FileViewer: React.FC<FileViewerProps> = ({
  sharedLinkUrl,
  fileName,
  reportError,
  children,
  logExposureCallback,
}) => {
  const apiV2Shim = useMemo(() => makeApiV2ClientShim(), []);
  useEffect(() => {
    logExposureCallback?.();
  }, [logExposureCallback]);

  const { reportPapEvent } = useMirageAnalyticsContext();
  const logger = useMemo(
    () => getFileViewerLogger(reportPapEvent),
    [reportPapEvent],
  );

  const [intl, setIntl] = useState<
    Awaited<ReturnType<typeof fetchFileViewerIntlForLocale>> | undefined
  >(undefined);

  useEffect(() => {
    const fetchIntl = async () => {
      try {
        const intl = await fetchFileViewerIntlForLocale(i18n.language);
        setIntl(intl);
      } catch (e) {
        reportError?.("Failed to fetch File Viewer intl: " + e);
      }
    };
    fetchIntl();
  }, [reportError]);

  const reportFileViewerError = useCallback(
    (err: FVError) => {
      reportError?.(`File Viewer error: ${err}`);
    },
    [reportError],
  );

  const fileViewerProps = useMemo((): FileViewerLibProps | undefined => {
    if (!intl) {
      return undefined;
    }
    return {
      apiV2ClientShim: apiV2Shim,
      logger,
      intl,
      optionalIOClients: {},
      featureConfig: {
        enable_fullscreen: false,
        keyboard_shortcuts: false,
        // Allows previewing paper docs.
        cloud_doc_preview_mode: "preview",
        preview_types: {
          [PreviewType.SsrDoc]: {
            scaleToFit: "width",
          },
        },
      },
      sourceSession: {
        source_context: "dash_assistant" as SourceContext,
        page_start_ts: performance.now(),
      },
      isLoggedIn: true,
    };
  }, [apiV2Shim, intl, logger]);

  const fileNameInternal = useMemo(() => {
    // This is a hack to ensure that the file name has the correct extension when
    // the shared link URL is a paper doc. Usually, Dash provides the title of the
    // document, but File Viewer expects the name to end in .paper. This should
    // probably be fixed internally in File Viewer, but for now we can work around
    // like this.
    const sharedLinkURL = new URL(sharedLinkUrl);
    if (!sharedLinkURL.pathname.endsWith(".paper")) {
      return fileName;
    } else {
      return fileName.endsWith(".paper") ? fileName : fileName + ".paper";
    }
  }, [sharedLinkUrl, fileName]);
  const previewProps: PreviewProps = useMemo(() => {
    return {
      sharedLinkInfo: {
        url: sharedLinkUrl,
        displayName: fileNameInternal,
      },
      callbacks: {
        onRenderFailed: reportFileViewerError,
      },
    };
  }, [fileNameInternal, reportFileViewerError, sharedLinkUrl]);

  return (
    <div className={classNames(style.FileViewerWrapper)}>
      {fileViewerProps && (
        <FileViewerLib {...fileViewerProps}>
          <div className={style.FileViewerPreviewContent}>
            {<Preview {...previewProps} />}
          </div>
          {children}
        </FileViewerLib>
      )}
    </div>
  );
};
